import type { Affiliation } from '@interfaces/affiliation';

const getAffiliationIds = (
  affiliations: Affiliation[] | undefined,
): Set<string> =>
  new Set(
    (affiliations ?? [])
      .map((affiliation) => affiliation.id)
      .filter((id): id is string => id != null),
  );

const hasSameAffiliationMembership = (
  serverAffiliations: Affiliation[] | undefined,
  localAffiliations: Affiliation[] | undefined,
): boolean => {
  const serverIds = getAffiliationIds(serverAffiliations);
  const localIds = getAffiliationIds(localAffiliations);

  if (serverIds.size !== localIds.size) {
    return false;
  }

  for (const id of serverIds) {
    if (!localIds.has(id)) {
      return false;
    }
  }

  return true;
};

export const mergeAccountAffiliations = (
  serverAffiliations: Affiliation[] | undefined,
  localAffiliations: Affiliation[] | undefined,
): Affiliation[] => {
  const server = serverAffiliations ?? [];

  if (!hasSameAffiliationMembership(server, localAffiliations)) {
    return server;
  }

  const localById = new Map(
    (localAffiliations ?? [])
      .filter((affiliation) => affiliation.id)
      .map((affiliation) => [affiliation.id as string, affiliation]),
  );

  return server.map((serverAffiliation) => {
    const localAllocation = serverAffiliation.id
      ? localById.get(serverAffiliation.id)?.allocation
      : undefined;

    if (localAllocation === undefined) {
      return serverAffiliation;
    }

    return { ...serverAffiliation, allocation: localAllocation };
  });
};
